type TRgbKeys = 'r' | 'g' | 'b';
type THslKeys = 'hue' | 'saturation' | 'lightness';
type TConvertType = 'rgb' | 'hsl';
type TStringValue = { stringValue: string };
type TDataReturnTypes = {
    rgb: TStringValue &
        {
            [Key in TRgbKeys]: number;
        };
    hsl: TStringValue &
        {
            [Key in THslKeys]: number;
        };
};

type TArgOverload<T extends TConvertType> = {
    (hex: string): TDataReturnTypes[T];
};

/**
 * Converts a 3 or 6 digit (not including pound sign) to a rgb() value
 */
export const hexToRGB: TArgOverload<'rgb'> = (hex: string): any => {
    if (typeof hex !== 'string' || (hex.length !== 4 && hex.length !== 7) || hex[0] !== '#') {
        throw new Error('Provided argument must be a valid hex code');
    }
    let r = '0';
    let g = '0';
    let b = '0';
    // 3 digits
    if (hex.length === 4) {
        r = `0x${hex[1]}${hex[1]}`;
        g = `0x${hex[2]}${hex[2]}`;
        b = `0x${hex[3]}${hex[3]}`;
        // 6 digits
    } else if (hex.length === 7) {
        r = `0x${hex[1]}${hex[2]}`;
        g = `0x${hex[3]}${hex[4]}`;
        b = `0x${hex[5]}${hex[6]}`;
    }

    return { r: +r, g: +g, b: +b, stringValue: `rgb(${+r},${+g},${+b})` };
};

/**
 * Converts a 7 digit hex to an array with rgba tuple values
 */
const rgbToTuple = (value: string) => +parseFloat((parseInt(value, 16) / 255).toString()).toFixed(2);

// #A73B8F -> [0.65, 0.23, 0.56, 1]
export const hexToRGBATuple = (hex: string) => {
    const colorValues = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    const result = [];

    if (colorValues?.length) {
        for (let i = 1; i <= 3; i++) {
            result.push(rgbToTuple(colorValues[i]));
        }
        result.push(1);
    }
    return result;
};

/**
 * Converts a 3 or 6 digit (not including pound sign) to a hsl() value
 */
export const hexToHSL: TArgOverload<'hsl'> = (hex: string): any => {
    const rgb = hexToRGB(hex);

    const r = rgb.r / 255;
    const g = rgb.g / 255;
    const b = rgb.b / 255;
    const cmin = Math.min(r, g, b);
    const cmax = Math.max(r, g, b);
    const delta = cmax - cmin;
    let hue = 0;
    let saturation = 0;
    let lightness = 0;

    if (delta === 0) hue = 0;
    else if (cmax === r) hue = ((g - b) / delta) % 6;
    else if (cmax === g) hue = (b - r) / delta + 2;
    else hue = (r - g) / delta + 4;

    hue = Math.round(hue * 60);

    if (hue < 0) hue += 360;

    lightness = (cmax + cmin) / 2;
    saturation = delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1));
    saturation = +(saturation * 100).toFixed(1);
    lightness = +(lightness * 100).toFixed(1);

    return { hue, saturation, lightness, stringValue: `hsl(${hue},${saturation}%,${lightness}%)` };
};

/**
 * Converts a 3 or 6 digit (not including pound sign) to a hsv() value
 */
export const hexToHSV = (hex: string) => {
    const rgb = hexToRGB(hex);
    const r = rgb.r / 255;
    const g = rgb.g / 255;
    const b = rgb.b / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let hue = 1;
    const d = max - min;
    const saturation = max === 0 ? 0 : d / max;
    const value = max;

    if (max === min) {
        hue = 0; // achromatic
    } else {
        switch (max) {
            case r:
                hue = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                hue = (b - r) / d + 2;
                break;
            case b:
                hue = (r - g) / d + 4;
                break;
        }
        hue /= 6;
    }

    return { hue: +hue.toFixed(3), saturation: +saturation.toFixed(3), value: +value.toFixed(3) };
};

/**
 * Returns the opacity and filter to use depending on if the color is a pastel or not
 */
export const getColorModifications = (saturation: number, value: number) => {
    if (saturation <= 0.11 && value >= 0.66) {
        return { opacity: 1, filter: 'brightness(0.8) saturate(6)' };
    }
    if (saturation <= 0.22 && value >= 0.66) {
        return { opacity: 0.5, filter: 'brightness(0.9) saturate(3)' };
    }
    return { opacity: 0.1, filter: 'none' };
};
